import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';

type CardStat = {
  value: string;
  label: string;
};

type FeatureCard = {
  title: string;
  description: string;
};

const featureIcons = [
  (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l7 3v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6l7-3z" />
    </svg>
  ),
  (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18" />
    </svg>
  ),
  (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .32 1.76l.05.05a2 2 0 0 1-2.83 2.83l-.05-.05A1.6 1.6 0 0 0 15 19.4a1.6 1.6 0 0 0-1 .6 1.6 1.6 0 0 1-2 0 1.6 1.6 0 0 0-1-.6 1.6 1.6 0 0 0-1.76.32l-.05.05a2 2 0 0 1-2.83-2.83l.05-.05A1.6 1.6 0 0 0 4.6 15a1.6 1.6 0 0 0-.6-1 1.6 1.6 0 0 1 0-2 1.6 1.6 0 0 0 .6-1 1.6 1.6 0 0 0-.32-1.76l-.05-.05a2 2 0 1 1 2.83-2.83l.05.05A1.6 1.6 0 0 0 9 4.6a1.6 1.6 0 0 0 1-.6 1.6 1.6 0 0 1 2 0 1.6 1.6 0 0 0 1 .6 1.6 1.6 0 0 0 1.76-.32l.05-.05a2 2 0 0 1 2.83 2.83l-.05.05A1.6 1.6 0 0 0 19.4 9c.23.37.52.68.85.9a1.6 1.6 0 0 1 0 2c-.33.22-.62.53-.85.9z" />
    </svg>
  ),
  (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 21h18M7 21V7h10v14M9 10h2M13 10h2M9 14h2M13 14h2" />
    </svg>
  )
];

const AboutUs: React.FC = () => {
  const { t } = useTranslation();
  const ArrowIcon = FiArrowRight as React.ComponentType<{ className?: string }>;

  const statsCards = t('aboutUsSection.statsCards', { returnObjects: true, defaultValue: [] }) as CardStat[];
  const featureCards = t('aboutUsSection.featureCards', { returnObjects: true, defaultValue: [] }) as FeatureCard[];

  return (
    <section className="py-28 bg-[#F7F8FA] relative overflow-hidden">
      <div className='absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal-50 rounded-full blur-[150px] opacity-50'></div>
      <div className='relative max-w-7xl mx-auto px-6'>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <div>
            <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.2em] mb-3">
              {t('home.about.badge')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight leading-tight">
              {t('home.about.title')} {t('home.about.titleHighlight')}
            </h2>
            <p className="text-[#64748B] mt-6 leading-relaxed text-base">
              {t('home.about.description1')}
            </p>
            <p className="text-[#94A3B8] mt-4 leading-relaxed text-sm">
              {t('home.about.description2')}
            </p>

            <div className="grid grid-cols-2 gap-4 mt-10">
              {statsCards.map((card, index) => (
                <div key={`${card.label}-${index}`} className="bg-white rounded-xl p-4 border border-[#E2E8F0]">
                  <p className="text-2xl font-bold text-[#0F172A]">{card.value}</p>
                  <p className="text-xs text-[#94A3B8] mt-1 font-medium">{card.label}</p>
                </div>
              ))}
            </div>

            <Link to="/about" className="inline-flex items-center gap-2 text-teal-600 font-semibold text-sm mt-8 hover:gap-3 transition-all">
              {t('home.about.learnMore')}
              <ArrowIcon className="h-5 w-5" />
            </Link>
          </div>

          <div className="space-y-4">
            {featureCards.map((item, index) => (
              <article key={`${item.title}-${index}`} className='flex gap-4 bg-white rounded-xl p-5 border border-[#E2E8F0] hover:border-teal-200 hover:shadow-md transition-all duration-300 group'>
                <div className='w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center shrink-0 text-teal-600 group-hover:bg-teal-500 group-hover:text-white transition-colors'>
                  {featureIcons[index % featureIcons.length]}
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] text-sm">{item.title}</h3>
                  <p className="text-[#64748B] text-sm mt-1 leading-relaxed">{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
